import { useEffect, useState } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';
import { CheckCircle2, XCircle, Info, AlertTriangle, Loader2 } from 'lucide-react';

function useHtmlTheme(): 'light' | 'dark' {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });
  useEffect(() => {
    const el = document.documentElement;
    const update = () => setTheme(el.classList.contains('dark') ? 'dark' : 'light');
    const mo = new MutationObserver(update);
    mo.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);
  return theme;
}

export function Toaster(props: ToasterProps) {
  const theme = useHtmlTheme();
  return (
    <Sonner
      theme={theme}
      position="bottom-right"
      offset="24px"
      gap={10}
      duration={3500}
      visibleToasts={4}
      icons={{
        success: <CheckCircle2 className="h-4 w-4" strokeWidth={2} />,
        error: <XCircle className="h-4 w-4" strokeWidth={2} />,
        info: <Info className="h-4 w-4" strokeWidth={2} />,
        warning: <AlertTriangle className="h-4 w-4" strokeWidth={2} />,
        loading: <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />,
      }}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            'group pointer-events-auto flex w-[calc(100vw-2rem)] items-start gap-2.5 bg-card/95 text-foreground border border-border/80 ring-1 ring-foreground/5 rounded-[var(--radius)] shadow-[0_18px_48px_-18px_hsl(var(--foreground)/0.34),0_8px_22px_-14px_hsl(var(--foreground)/0.28),0_1px_0_hsl(var(--background)/0.55)_inset] px-4 py-3 text-[13px] leading-snug backdrop-blur-md sm:w-auto sm:min-w-[280px] sm:max-w-[360px]',
          title: 'font-medium tracking-tight text-[13px]',
          description: 'text-muted-foreground text-[12.5px] mt-0.5 leading-relaxed',
          icon: 'shrink-0 mt-[1px] grid place-items-center h-5 w-5 rounded-full',
          success:
            "[&_[data-icon]]:bg-signal/12 [&_[data-icon]]:text-signal [&_[data-icon]]:ring-1 [&_[data-icon]]:ring-inset [&_[data-icon]]:ring-signal/25",
          error:
            "[&_[data-icon]]:bg-destructive/12 [&_[data-icon]]:text-destructive [&_[data-icon]]:ring-1 [&_[data-icon]]:ring-inset [&_[data-icon]]:ring-destructive/25",
          info:
            "[&_[data-icon]]:bg-info/12 [&_[data-icon]]:text-info [&_[data-icon]]:ring-1 [&_[data-icon]]:ring-inset [&_[data-icon]]:ring-info/25",
          warning:
            "[&_[data-icon]]:bg-warn/12 [&_[data-icon]]:text-warn [&_[data-icon]]:ring-1 [&_[data-icon]]:ring-inset [&_[data-icon]]:ring-warn/25",
          loading:
            "[&_[data-icon]]:bg-muted [&_[data-icon]]:text-muted-foreground [&_[data-icon]]:ring-1 [&_[data-icon]]:ring-inset [&_[data-icon]]:ring-border",
          actionButton:
            'ml-auto rounded-md bg-foreground text-background text-[11.5px] font-medium px-2 py-1 hover:opacity-90 transition-opacity',
          cancelButton:
            'rounded-md bg-muted text-muted-foreground text-[11.5px] font-medium px-2 py-1 hover:bg-accent transition-colors',
          closeButton:
            'bg-transparent border-0 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md p-0.5 transition-colors',
          content: 'flex-1 min-w-0',
        },
      }}
      {...props}
    />
  );
}
